import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import { Credentials } from "../models/Credentials";
import { authRepository } from "./AuthRepository";
import { getMeasurementSystem } from "../utils/Utils";

declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuthentication?: boolean;
        skipAuthRefresh?: boolean;
    }

    export interface InternalAxiosRequestConfig {
        skipAuthentication?: boolean;
        skipAuthRefresh?: boolean;
    }
}

interface ErrorResponse {
  message?: string;
}

export class UserNotFoundOnLogin extends Error {}
export class InvalidOtp extends Error {}

export class Repository {
    static offlineMode = false;
    static client: AxiosInstance;
    static credentials: Credentials | null = null;
    static isRefreshing = false;
    static failedQueue: any[] = [];
    static onLogout: () => void;

    static processQueue(error: any = null) {
        Repository.failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve();
            }
        });
        Repository.failedQueue = [];
    }

    static init() {
        Repository.client = axios.create({
            baseURL: import.meta.env.VITE_BASE_URL
        });

        Repository.client.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                if (Repository.credentials?.accessToken && !config.skipAuthentication) {
                    config.headers.Authorization = `Bearer ${Repository.credentials?.accessToken}`;
                }
                const userLanguage = navigator.language || 'en-US';
                const bcp47Locale = userLanguage.includes('-') ? userLanguage : `${userLanguage}-${userLanguage.toUpperCase()}`;
                config.headers['x-client-platform'] = 'web';
                config.headers['x-locale'] = bcp47Locale;
                config.headers['x-measurement-system'] = getMeasurementSystem();
                config.headers['x-device-id'] = navigator.userAgent;
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Add response interceptor
        Repository.client.interceptors.response.use(
            response => response,
            async (error: AxiosError<ErrorResponse>) => {
                const originalRequest = error.config;
                
                if (!originalRequest) {
                    return Promise.reject(error);
                }

                if(error.response?.status === 400)
                {
                    if(error.response?.data?.message === "User doesn't exist")
                        throw new UserNotFoundOnLogin();
                    if(error.response?.data?.message === "Invalid OTP")
                        throw new InvalidOtp();
                }
                if (error.response?.status === 401) {
                    if (originalRequest.skipAuthRefresh) {
                        return Promise.reject(error);
                    }

                    if (Repository.isRefreshing) {
                        return new Promise((resolve, reject) => {
                            Repository.failedQueue.push({ resolve, reject });
                        })
                            .then(() => {
                                return Repository.client(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    Repository.isRefreshing = true;

                    try {
                        if(Repository.credentials)
                        {
                            const { credentials } = await authRepository.refreshToken(Repository.credentials);
                            Repository.setCredentials(credentials);
                        
                            Repository.processQueue();
                            Repository.isRefreshing = false;

                            return Repository.client(originalRequest);
                        }
                        else
                        {
                            throw new Error('No credentials available');
                        }
                    } catch (refreshError) {
                        Repository.processQueue(refreshError);
                        Repository.isRefreshing = false;
                        Repository.logout();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    static setCredentials(credentials: Credentials | null): void {
        Repository.credentials = credentials;
    }

    static setStreamToken(streamToken: string | null): void {        
        if(Repository.credentials)
        {
            if(streamToken)
            {
                Repository.credentials.streamToken = streamToken;
            }
            else
            {
                Repository.credentials.streamToken = null;
            }

        }
    }

    static logout(): void {
        Repository.setCredentials(null);

        if(Repository.onLogout)
            Repository.onLogout();
    }
}
